import * as React from 'react';
import { StepperButton, StepperField, StepperWrapper } from '../internal';
import { filterInputByType, formatValue, normalizeValue } from '../utils';

/**
 * [Ссылка на макет](https://www.figma.com/design/4UM8zoowH9YGMM7fSK0rhD/%F0%9F%8C%90-Web-Components?node-id=59442-5735&t=q3ADmdLgpp2XtHMk-4)
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const Stepper = _ref => {
  let {
    title,
    value,
    min,
    max,
    fullWidth = false,
    errorMessage,
    disabled = false,
    invalid = false,
    size = 'M',
    step = 1,
    dataTestId = 'stepper',
    onChange,
    onIncrement,
    onDecrement,
    inputType = 'decimal'
  } = _ref;
  const [inputValue, setInputValue] = React.useState(() => formatValue(value));
  const currentNumberValue = parseFloat(inputValue.replace(',', '.')) || 0;
  const isDecrementDisabled = disabled || currentNumberValue <= min;
  const isIncrementDisabled = disabled || currentNumberValue >= max;
  React.useEffect(() => {
    if (normalizeValue(currentNumberValue, min, max) !== value) {
      setInputValue(formatValue(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const handleChange = React.useCallback((next, customHandler) => {
    if (customHandler) {
      customHandler(next);
    } else {
      onChange(next);
    }
  }, [onChange]);
  const handleIncrement = React.useCallback(() => {
    if (disabled) {
      return;
    }
    const next = normalizeValue(currentNumberValue + step, min, max);
    setInputValue(formatValue(next));
    handleChange(next, onIncrement);
  }, [disabled, currentNumberValue, step, min, max, handleChange, onIncrement]);
  const handleDecrement = React.useCallback(() => {
    if (disabled) {
      return;
    }
    const next = normalizeValue(currentNumberValue - step, min, max);
    setInputValue(formatValue(next));
    handleChange(next, onDecrement);
  }, [disabled, currentNumberValue, step, min, max, handleChange, onDecrement]);
  const handleInputChange = React.useCallback(event => {
    const rawInput = filterInputByType(event.target.value, inputType);
    const clamped = normalizeValue(rawInput, min, max);
    const newInputValue = clamped === max ? formatValue(clamped) : rawInput;
    setInputValue(newInputValue);
    onChange(clamped);
  }, [min, max, onChange, inputType]);
  const commitInputValue = React.useCallback(() => {
    if (inputValue.trim() === '') {
      setInputValue(formatValue(value));
      return;
    }
    const clamped = normalizeValue(inputValue, min, max);
    setInputValue(formatValue(clamped));
    if (clamped !== value) {
      onChange(clamped);
    }
  }, [inputValue, min, max, value, onChange]);
  const handleInputBlur = React.useCallback(() => commitInputValue(), [commitInputValue]);
  const handleInputFieldKeyDown = React.useCallback(event => {
    if (event.key === 'Enter') {
      commitInputValue();
    }
  }, [commitInputValue]);
  const handleKeyboardFocus = React.useCallback(() => {
    setInputValue('');
  }, []);
  return /*#__PURE__*/_jsx(StepperWrapper, {
    title: title,
    errorMessage: errorMessage,
    fullWidth: fullWidth,
    disabled: disabled,
    invalid: invalid,
    size: size,
    dataTestId: dataTestId,
    children: /*#__PURE__*/_jsx(StepperField, {
      dataTestId: dataTestId,
      value: inputValue || '',
      fullWidth: fullWidth,
      size: size,
      step: step,
      min: min,
      max: max,
      onChange: handleInputChange,
      onBlur: handleInputBlur,
      onKeyDown: handleInputFieldKeyDown,
      decrementButton: /*#__PURE__*/_jsx(StepperButton, {
        size: size,
        disabled: isDecrementDisabled,
        stepperType: "decrement",
        onClick: handleDecrement
      }),
      incrementButton: /*#__PURE__*/_jsx(StepperButton, {
        size: size,
        disabled: isIncrementDisabled,
        stepperType: "increment",
        onClick: handleIncrement
      }),
      disabled: disabled,
      invalid: invalid,
      onKeyboardFocus: handleKeyboardFocus
    })
  });
};